import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Popover,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import apiClient from '../../api/apiClient';
import { fetchDecisionStats } from '../../api/trades';
import { extractErrorMessage } from '../../api/extractErrorMessage';

import DWLFLoader from '../../components/DWLFLoader';
import DecisionCard from './today/DecisionCard';
import DecisionStack from './today/DecisionStack';
import { fmtR } from '../trades/reviewHelpers';

/*
 * Today — the trading day's front door.
 *
 * Answers the morning questions in order: is anything waiting on me
 * (planned signals -> review), how's the open book, and is my judgement
 * holding up (decision scorecard). Market-level state lives on /markets;
 * this page is about YOUR positions and YOUR decisions.
 */

const MONO = '"Space Mono", monospace';
const GROTESK = '"Space Grotesk", sans-serif';
const CYAN = 'var(--dwlf-accent)';
// Scheme tokens: the light-mode up/down/warn are dark enough that chip text (var(--dwlf-bg)) stays readable.
const GREEN = 'var(--dwlf-up)';
const RED = 'var(--dwlf-down)';
const AMBER = 'var(--dwlf-warn)';
const MUTED = 'var(--dwlf-ink-50)';

const fmtPrice = (v) => (v === null || v === undefined || !Number.isFinite(Number(v)) ? '—' : Number(v).toFixed(2));

// Chart routes use the DASHED symbol form (BTC-USD); the /ranges API returns
// slashes (BTC/USD). Null-guarded so a payload row missing `symbol` can't throw
// from a click handler (rangeSort coerces the same field defensively). Matches
// marketDetailPage's own `cleaned.replace('/', '-')` — no encodeURIComponent, so
// the route param round-trips exactly as the chart page expects. An optional
// timeframe becomes `?timeframe=` (marketDetailPage reads it and picks that
// chart) so a weekly range row opens the weekly chart.
const chartPathFor = (symbol, timeframe) => (symbol
  ? `/markets/${String(symbol).replace(/\//g, '-')}${timeframe ? `?timeframe=${timeframe}` : ''}`
  : null);
const rColor = (r) => (r > 0.005 ? GREEN : r < -0.005 ? RED : MUTED);
// Macro Weather regime + per-dial lean → colour.
const regimeColorOf = (regime) => (regime === 'RISK_ON' ? GREEN : regime === 'RISK_OFF' ? RED : AMBER);
const leanColorOf = (lean) => (lean === 'risk_on' ? GREEN : lean === 'risk_off' ? RED : MUTED);
const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
// Sector-cohort signal → colour + label. basing_process gets the accent colour —
// it's the "cycle low forming" buy telescope; topping_process is the warning.
const COHORT_SIGNALS = {
  aligned_bull: { color: GREEN, label: 'Aligned bull' },
  aligned_bear: { color: RED, label: 'Aligned bear' },
  topping_process: { color: AMBER, label: 'Topping process' },
  basing_process: { color: CYAN, label: 'Basing process' },
  split: { color: MUTED, label: 'Split' },
  insufficient: { color: MUTED, label: 'No read' },
};
const COHORT_NAMES = {
  mag7: 'Mag 7', semis: 'Semis', ai_software: 'AI software', ai_froth: 'AI froth',
  metals: 'Metals', gold_miners: 'Gold miners', btc_adjacents: 'BTC adjacents',
  crypto_majors: 'Crypto majors', us_indices: 'US indices', energy: 'Energy',
};
// Sort: changed signals first, then the process states, then aligned, then noise.
// Unknown/new signal values rank LAST (not -1 = top) until the UI learns them.
const COHORT_ORDER = ['topping_process', 'basing_process', 'aligned_bear', 'aligned_bull', 'split', 'insufficient'];
const cohortRank = (s) => { const i = COHORT_ORDER.indexOf(s); return i === -1 ? COHORT_ORDER.length : i; };
const cohortSort = (a, b) => (Boolean(b.changed) - Boolean(a.changed)) || (cohortRank(a.signal) - cohortRank(b.signal));
const fmtCounts = (c) => (c ? `${c.bull ?? 0}↑ ${c.mixed ?? 0}~ ${c.bear ?? 0}↓` : '—');

// Live range position → colour + label. near_floor is the buy telescope (cyan
// accent, like a cycle low forming); near_ceiling is the caution.
const RANGE_STATES = {
  near_floor: { color: CYAN, label: 'At floor' },
  near_ceiling: { color: AMBER, label: 'At ceiling' },
  mid_range: { color: MUTED, label: 'Mid-range' },
  above: { color: GREEN, label: 'Broke out ↑' },
  below: { color: RED, label: 'Broke down ↓' },
};
// Order: the actionable edges first (floor = entries + DCL confluence, ceiling
// = caution), then the breaks, then mid-range noise. Unknown states rank last.
const RANGE_ORDER = ['near_floor', 'near_ceiling', 'below', 'above', 'mid_range'];
const rangeRank = (s) => { const i = RANGE_ORDER.indexOf(s); return i === -1 ? RANGE_ORDER.length : i; };
const rangeSort = (a, b) => (rangeRank(a.live?.liveState) - rangeRank(b.live?.liveState))
  || String(a.symbol).localeCompare(String(b.symbol));

const headCell = { fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, fontWeight: 600 };

// Relative-day label for the calendar strip: Today / Tomorrow / a weekday
// for the next week / a short date beyond that.
const relDayLabel = (daysUntil, dateStr) => {
  if (daysUntil === 0) {return 'Today';}
  if (daysUntil === 1) {return 'Tomorrow';}
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) {return `in ${daysUntil}d`;}
  if (daysUntil <= 6) {return d.toLocaleDateString('en-GB', { weekday: 'short' });}
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const SectionTitle = ({ children, primer, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
    <Typography sx={{ fontFamily: GROTESK, fontSize: '1.1rem', fontWeight: 600, color: 'var(--dwlf-text)' }}>
      {children}
    </Typography>
    {primer && <InfoPrimer title={primer.title} lines={primer.lines} />}
    {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
  </Box>
);

// ⓘ "How to read this" — a small popover primer for the composite cards, so
// the instrument explains itself (the per-item hovers explain today's values;
// this explains what the instrument IS).
const InfoPrimer = ({ title, lines = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <IconButton
        size="small"
        aria-label={title}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: MUTED, p: 0.25, '&:hover': { color: 'var(--dwlf-text)' } }}
      >
        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 440, p: 2, backgroundColor: 'var(--dwlf-surface)', border: '1px solid var(--dwlf-ink-12)' } } }}
      >
        <Typography sx={{ fontFamily: GROTESK, fontSize: '0.9rem', fontWeight: 700, color: 'var(--dwlf-text)', mb: 1 }}>
          {title}
        </Typography>
        {lines.map((line, i) => (
          <Typography key={i} sx={{ fontFamily: MONO, fontSize: '0.72rem', color: 'var(--dwlf-ink-75)', mb: 0.75, lineHeight: 1.55, '&:last-child': { mb: 0 } }}>
            {line}
          </Typography>
        ))}
      </Popover>
    </>
  );
};

// Shared styling for the info tooltips on dials / cohort rows.
const TOOLTIP_SLOT_PROPS = (maxWidth) => ({
  tooltip: { sx: { fontFamily: MONO, fontSize: '0.7rem', lineHeight: 1.5, maxWidth, backgroundColor: 'var(--dwlf-surface)', border: '1px solid var(--dwlf-ink-12)' } },
});

// Fallback tooltip copy per dial / cohort signal — shown when the API doesn't
// supply a live reason, so a help cursor never leads to an empty tooltip.
// The final generic strings catch unknown/new keys the UI hasn't learned yet.
const DIAL_FALLBACK_GENERIC = 'A macro dial feeding the regime score — green leans risk-ON, red risk-OFF.';
const COHORT_FALLBACK_GENERIC = 'Group trend read across the members\' daily + weekly swing ladders.';
const DIAL_FALLBACKS = {
  dollar: 'Dollar (UUP): rising = money hiding in the reserve currency = risk-OFF; falling = risk appetite returning.',
  yen: 'Yen (JPY/USD · EUR/JPY): a strengthening yen = carry trades unwinding = risk-OFF; a weak yen = carry ON.',
  rates: 'Rates (TLT): long bonds falling = yields rising = conditions tightening = risk-OFF.',
  vol: 'Volatility (VXX): rising = fear being bought = risk-OFF; falling = calm (or complacency).',
  breadth: 'Breadth (EEM · IWM): emerging markets + small caps falling = the periphery cracking = risk-OFF. The edges break before the core.',
};
const COHORT_SIGNAL_FALLBACKS = {
  aligned_bull: 'Dailies and weeklies agree bullish across the group - the uptrend is in force.',
  aligned_bear: 'Dailies and weeklies agree bearish across the group - the downtrend is in force.',
  topping_process: 'Dailies rolling over inside still-bullish weeklies - how a top forms, one name at a time.',
  basing_process: 'Dailies turning up inside beaten-down weeklies - how a bottom looks while forming. The accumulation tell.',
  split: 'Members disagree - no group-wide read.',
  insufficient: 'Too few members reporting to give a group-wide read.',
};

const MACRO_WEATHER_PRIMER = {
  title: 'How to read Macro Weather',
  lines: [
    'Five macro dials — the dollar, the yen, long-term rates, volatility, and market breadth — each read on their last 5–10 days of momentum, then combined into one risk-ON / risk-OFF regime.',
    'Dollar (UUP) rising → risk-OFF: money hiding in the reserve currency.',
    'Yen (JPY/USD · EUR/JPY) strengthening → risk-OFF: carry trades unwinding.',
    'Rates (TLT falling, long yields rising) → risk-OFF: conditions tightening.',
    'Volatility (VXX) rising → risk-OFF: fear being bought.',
    'Breadth (EEM · IWM) falling → risk-OFF: the periphery cracking first — the edges break before the core.',
    'The dials sum to a weighted score (dollar and yen count double): ≥ +2 is RISK-ON, ≤ −2 is RISK-OFF, in between is neutral/mixed. Hover any dial chip for why it leans the way it does today.',
  ],
};

const COHORTS_PRIMER = {
  title: 'How to read Sector cohorts',
  lines: [
    'Each row is a group of related names (Mag7, semis, metals, gold miners…). DWLF reads every member\'s daily and weekly swing-ladder trend and asks: is the group moving as one?',
    'Aligned bull / bear — dailies and weeklies agree across the group; the trend is in force.',
    'Topping process (amber) — dailies rolling over inside still-bullish weeklies: how a top forms, one name at a time.',
    'Basing process (cyan) — dailies turning up inside beaten-down weeklies: how a bottom looks while it\'s forming — the accumulation tell.',
    'Split — members disagree · No read — too few members reporting. Neither gives a group-wide signal (dimmed).',
    'NEW means the signal just changed; the day count is time in the current state; D/W are member counts up/flat/down per timeframe. Hover a row for its full explanation.',
  ],
};

// Per-timeframe primer — the toggle shows daily OR weekly ranges, and the two
// layers read differently (daily = tactical + DCL confluence; weekly = structural).
const RANGES_PRIMER = {
  daily: {
    title: 'How to read Ranges (daily)',
    lines: [
      'Bounded support/resistance ranges from confirmed DAILY cycle pivots — a "where in a range are we" read, not a trade signal. The tactical layer.',
      'At floor (bottom of the range) → significant if it lines up with a daily-cycle-low window: the high-value confluence for an entry.',
      'At ceiling (top) → extended; think twice about entering here.',
      'Broke out ↑ / broke down ↓ → price has left the range; watch for a pullback to retest the edge.',
      'Position % is where price sits in the range (0% = floor, 100% = ceiling); the pair of numbers is the range itself.',
      'Symbols not in a clear range simply don\'t appear — "not in a range" is a valid, common answer.',
    ],
  },
  weekly: {
    title: 'How to read Ranges (weekly)',
    lines: [
      'Bounded support/resistance ranges from confirmed WEEKLY cycle pivots — the STRUCTURAL layer. Most assets trend on the weekly (higher-high ladders → no box), so the few that show are the classic dead-money-sideways names.',
      'Read it against the daily: AGREEMENT = high conviction; DIVERGENCE (e.g. daily at-ceiling but weekly mid) = "tactically extended, structurally mid".',
      'At floor / at ceiling → near a structural edge; broke out ↑ / down ↓ → a structural break, watch for a retest.',
      'Position % is where price sits in the range (0% = floor, 100% = ceiling); the pair of numbers is the range itself.',
      'Symbols trending on the weekly simply don\'t appear — "not in a range" is the common, correct answer here.',
    ],
  },
};

const TodayPage = () => {
  const navigate = useNavigate();
  const [planned, setPlanned] = useState(null);
  const [skippedRecent, setSkippedRecent] = useState([]);
  const [decidedHere, setDecidedHere] = useState([]); // { tradeId, kind, symbol } made on this page
  const isPhone = useMediaQuery('(max-width: 599px)');
  const [openTrades, setOpenTrades] = useState(null);
  const [marks, setMarks] = useState({}); // symbol -> latest close
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [macroWeather, setMacroWeather] = useState(null);
  const [cohortBreadth, setCohortBreadth] = useState(null);
  const [ranges, setRanges] = useState(null);
  const [rangeTf, setRangeTf] = useState('daily'); // Ranges section: daily (tactical) | weekly (structural)
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      apiClient.get('/trades', { params: { status: 'planned', limit: 10 } }),
      apiClient.get('/trades', { params: { status: 'open', limit: 20 } }),
      fetchDecisionStats().catch(() => null), // scorecard is additive
      apiClient.get('/trades', { params: { status: 'skipped', limit: 20 } }).catch(() => null), // 'Logged today' is additive
    ])
      .then(([plannedRes, openRes, statsRes, skippedRes]) => {
        if (cancelled) {return;}
        setPlanned(plannedRes.data?.items || plannedRes.data?.trades || []);
        setOpenTrades(openRes.data?.items || openRes.data?.trades || []);
        setStats(statsRes);
        setSkippedRecent(skippedRes?.data?.items || skippedRes?.data?.trades || []);
      })
      .catch((e) => {
        if (!cancelled) {setError(extractErrorMessage(e, 'Failed to load today view'));}
      });
    return () => { cancelled = true; };
  }, []);

  // Upcoming macro events (this-week strip). Additive context — fetched from
  // the lightweight /economic-calendar endpoint (dates only, fast), separate
  // from the gating loads above so it never blocks or fails the page.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/economic-calendar', { params: { days: 14 } })
      .then((res) => { if (!cancelled) { setUpcoming(res.data?.events || []); } })
      .catch(() => { /* additive — stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Macro Weather — the risk-on/off regime (dollar/yen/rates/vol/breadth) from
  // the lightweight /macro-weather endpoint. Additive context, silent on failure.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/macro-weather')
      .then((res) => { if (!cancelled) { setMacroWeather(res.data || null); } })
      .catch(() => { /* additive — stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Sector cohorts — per-group swing-ladder direction with signal transitions.
  // Additive context, silent on failure (mirrors the macro-weather pattern).
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/cohort-breadth')
      .then((res) => { if (!cancelled) { setCohortBreadth(res.data || null); } })
      .catch(() => { /* additive — stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Ranges — symbols currently in a range (BOTH timeframes) + where price sits
  // in it, with the live position recomputed from the latest close. Additive
  // context, silent on failure. The Daily/Weekly toggle splits the list in render.
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/ranges') // both timeframes in one call; split client-side by the toggle
      .then((res) => { if (!cancelled) { setRanges(res.data?.ranges || []); } })
      .catch(() => { /* additive — stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Live marks for the open book — latest daily candle per symbol.
  useEffect(() => {
    if (!openTrades?.length) {return undefined;}
    let cancelled = false;
    const symbols = [...new Set(openTrades.map((t) => t.assetSymbol).filter(Boolean))];
    Promise.all(symbols.map((symbol) =>
      apiClient
        .get(`/market-data/${encodeURIComponent(symbol.replace('/', '-'))}`, { params: { timeframe: 'daily', limit: 1 } })
        .then((res) => {
          const candles = res.data?.candles || res.data || [];
          const last = candles[candles.length - 1];
          return [symbol, last ? Number(last.close) : null];
        })
        .catch(() => [symbol, null])
    )).then((entries) => {
      if (!cancelled) {setMarks(Object.fromEntries(entries));}
    });
    return () => { cancelled = true; };
  }, [openTrades]);

  const openRows = useMemo(() => (openTrades || []).map((t) => {
    const mark = marks[t.assetSymbol];
    const entry = Number(t.entryPrice);
    const stop = Number(t.initialStop);
    const dir = t.direction === 'short' ? -1 : 1;
    const hasRisk = Number.isFinite(entry) && Number.isFinite(stop) && entry !== stop;
    const openR = hasRisk && Number.isFinite(mark) ? (dir * (mark - entry)) / Math.abs(entry - stop) : null;
    return { ...t, mark, openR };
  }), [openTrades, marks]);

  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  // Ranges split by timeframe for the Daily/Weekly toggle. filter() returns a
  // fresh array, so sorting it in place needs no defensive copy.
  const allRanges = ranges || [];
  const shownRanges = allRanges.filter((r) => r.timeframe === rangeTf).sort(rangeSort);

  // A decided trade leaves the queue at once; the log shows it from this page's record until the next
  // load brings it back from the API (where it is the source of truth).
  const onDecided = (tradeId, decision) => {
    setPlanned((p) => (p || []).filter((t) => t.tradeId !== tradeId));
    if (decision) setDecidedHere((d) => [...d, { tradeId, kind: decision.kind, symbol: decision.trade.assetSymbol }]);
    // A confirm opens a position and every decision moves the scorecard: reload both, which on
    // the old flow happened by returning from the Review page.
    Promise.all([
      apiClient.get('/trades', { params: { status: 'open', limit: 20 } }),
      fetchDecisionStats().catch(() => null),
    ])
      .then(([openRes, statsRes]) => {
        setOpenTrades(openRes.data?.items || openRes.data?.trades || []);
        if (statsRes) setStats(statsRes);
      })
      .catch(() => { /* the book stays as it was; the next load corrects it */ });
  };
  const queue = planned || [];
  const loggedToday = (() => {
    const isToday = (iso) => iso && new Date(iso).toDateString() === new Date().toDateString();
    const fromApi = [
      ...(skippedRecent || []).filter((t) => isToday(t.skippedAt)).map((t) => ({ tradeId: t.tradeId, kind: 'Skipped', symbol: t.assetSymbol })),
      ...(openTrades || []).filter((t) => isToday(t.entryAt)).map((t) => ({ tradeId: t.tradeId, kind: 'Confirmed', symbol: t.assetSymbol })),
    ];
    const seen = new Set(decidedHere.map((d) => d.tradeId));
    return [...decidedHere, ...fromApi.filter((d) => !seen.has(d.tradeId))].map((d) => ({ ...d, key: d.tradeId }));
  })();

  if (error) {
    return (
      <Box p={3}><Alert severity="error">{error}</Alert></Box>
    );
  }

  if (planned === null || openTrades === null) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  const judged = stats ? (stats.matrix.tookRight + stats.matrix.tookWrong + stats.matrix.skipRight + stats.matrix.skipWrong) : 0;
  const rightCalls = stats ? (stats.matrix.tookRight + stats.matrix.skipRight) : 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography sx={{ fontFamily: GROTESK, fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--dwlf-text)' }}>
          Today
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
          {today} · your positions and decisions — market state lives in Markets
        </Typography>
      </Box>

      {/* This week — scheduled macro events on the horizon (additive context) */}
      {upcoming.length > 0 && (
        <Box mb={4}>
          <SectionTitle>This week</SectionTitle>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {upcoming.map((ev) => {
              const isHigh = ev.impact === 'high';
              return (
                <Paper
                  key={`${ev.date}-${ev.event}`}
                  sx={{
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    minWidth: 150,
                    borderLeft: `3px solid ${isHigh ? CYAN : 'var(--dwlf-ink-20)'}`,
                  }}
                >
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: isHigh ? CYAN : MUTED, fontWeight: 600 }}>
                    {relDayLabel(ev.daysUntil, ev.date)}
                  </Typography>
                  <Typography sx={{ fontFamily: GROTESK, fontSize: '0.9rem', fontWeight: 600, color: 'var(--dwlf-text)' }}>
                    {ev.event}
                  </Typography>
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED }}>
                    {ev.region}{ev.time ? ` · ${ev.time}` : ''}
                  </Typography>
                </Paper>
              );
            })}
          </Box>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, mt: 1 }}>
            Symbols at a cycle decision point into these events show in the{' '}
            <Box component="span" sx={{ color: CYAN, cursor: 'pointer' }} onClick={() => navigate('/markets/overview')}>
              Markets briefing
            </Box>.
          </Typography>
        </Box>
      )}

      {/* Macro weather — the risk-on/off regime the market is trading inside (additive) */}
      {macroWeather?.regime && (
        <Box mb={4}>
          <SectionTitle primer={MACRO_WEATHER_PRIMER}>Macro weather</SectionTitle>
          <Paper sx={{ p: 2.5, borderLeft: `3px solid ${regimeColorOf(macroWeather.regime)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label={macroWeather.label || macroWeather.regime}
                size="small"
                sx={{ backgroundColor: regimeColorOf(macroWeather.regime), color: 'var(--dwlf-bg)', fontFamily: GROTESK, fontWeight: 700 }}
              />
              {Number.isFinite(macroWeather.score) && (
                <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: MUTED }}>
                  {macroWeather.score > 0 ? '+' : ''}{macroWeather.score}
                  {Number.isFinite(macroWeather.maxScore) ? ` / ±${macroWeather.maxScore}` : ''}
                </Typography>
              )}
              {macroWeather.asOf && (
                <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED }}>
                  as of {fmtDate(macroWeather.asOf)}
                </Typography>
              )}
            </Box>
            {macroWeather.summary && (
              <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: 'var(--dwlf-ink-75)', mb: 1.5, lineHeight: 1.5 }}>
                {macroWeather.summary}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(macroWeather.dials || []).map((d, i) => (
                <Tooltip
                  key={d.key || i}
                  title={d.why || DIAL_FALLBACKS[d.key] || DIAL_FALLBACK_GENERIC}
                  arrow
                  enterTouchDelay={0}
                  slotProps={TOOLTIP_SLOT_PROPS(320)}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 1, backgroundColor: 'var(--dwlf-ink-04)', cursor: 'help' }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: leanColorOf(d.lean), flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: GROTESK, fontSize: '0.8rem', color: 'var(--dwlf-text)', fontWeight: 600 }}>
                      {d.label}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          {/* Action queue — the only section that demands something of you. Decided in place (DWLF-302). */}
          <Box mb={4}>
            <SectionTitle>
              Awaiting your decision
              {queue.length > 0 && (
                <Chip label={queue.length} size="small" sx={{ ml: 1.5, backgroundColor: CYAN, color: 'var(--dwlf-bg)', fontWeight: 700 }} />
              )}
            </SectionTitle>
            {queue.length === 0 ? (
              <Paper sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
                  {loggedToday.length ? 'Queue clear. Every call is logged with its context.' : 'No signals awaiting review. New strategy signals land here (and on Telegram).'}
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isPhone && queue.length > 1
              ? <DecisionStack queue={queue} onDecided={onDecided} />
              : queue.map((t) => <DecisionCard key={t.tradeId} trade={t} onDecided={onDecided} />)}
              </Box>
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          {/* Logged today — decisions recorded today, whether made here or on the Review page */}
          <Box mb={4}>
            <SectionTitle>Logged today</SectionTitle>
            <Paper sx={{ p: 2 }}>
              {loggedToday.length === 0 ? (
                <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: MUTED }}>
                  Nothing decided yet today. Every confirm, resize or skip lands here.
                </Typography>
              ) : (
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {loggedToday.map((d) => (
                    <Box component="li" key={d.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontFamily: MONO, fontSize: '0.8rem' }}>
                      <Box component="span" sx={{ color: 'var(--dwlf-text)' }}>{d.symbol}</Box>
                      <Box component="span" sx={{ color: d.kind === 'Skipped' ? 'var(--dwlf-ink-60)' : 'var(--dwlf-accent)' }}>{d.kind}</Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography component={RouterLink} to="/trades" sx={{ display: 'inline-block', mt: 1.5, fontFamily: MONO, fontSize: '0.7rem', color: CYAN, textDecoration: 'none' }}>
                Full log →
              </Typography>
            </Paper>
          </Box>
          {/* Open book */}
          <Box mb={4}>
            <SectionTitle>Open positions</SectionTitle>
            {openRows.length === 0 ? (
              <Paper sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
                  Flat. No open positions.
                </Typography>
              </Paper>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headCell}>Asset</TableCell>
                      <TableCell sx={headCell}>Side</TableCell>
                      <TableCell sx={headCell} align="right">Entry</TableCell>
                      <TableCell sx={headCell} align="right">Stop</TableCell>
                      <TableCell sx={headCell} align="right">Last</TableCell>
                      <TableCell sx={headCell} align="right">Open R</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openRows.map((t) => (
                      <TableRow
                        key={t.tradeId}
                        onClick={() => navigate(`/trades/${t.tradeId}`)}
                        sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'background.surface' } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{t.assetSymbol}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={t.direction} size="small" color={t.direction === 'long' ? 'success' : 'error'} sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
                        </TableCell>
                        <TableCell align="right">{fmtPrice(t.entryPrice)}</TableCell>
                        <TableCell align="right">{fmtPrice(t.initialStop)}</TableCell>
                        <TableCell align="right">{fmtPrice(t.mark)}</TableCell>
                        <TableCell align="right" sx={{ color: rColor(t.openR), fontWeight: 600 }}>
                          {t.openR === null ? '…' : fmtR(t.openR)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          {/* Decision scorecard */}
          {stats && (
            <Box mb={4}>
              <SectionTitle>Decision scorecard</SectionTitle>
              <Card sx={{ border: '1px solid var(--dwlf-ink-12)' }}>
                <CardContent>
                  {judged > 0 ? (
                    <>
                      <Typography sx={{ fontFamily: GROTESK, fontSize: '2rem', fontWeight: 700, color: 'var(--dwlf-text)' }}>
                        {Math.round((rightCalls / judged) * 100)}%
                      </Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: '0.75rem', color: MUTED, mb: 2 }}>
                        {rightCalls} of {judged} resolved decisions right
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: rColor(stats.totals.confirmed.netRealizedR) }}>
                          takes {fmtR(stats.totals.confirmed.netRealizedR)} realized
                        </Typography>
                        <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: rColor(stats.totals.skipped.netSavedR) }}>
                          skips {fmtR(stats.totals.skipped.netSavedR)} saved
                        </Typography>
                        {stats.matrix.tookOpen > 0 && (
                          <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: MUTED }}>
                            {stats.matrix.tookOpen} open ({fmtR(stats.totals.confirmed.netOpenR)} mark)
                          </Typography>
                        )}
                      </Box>
                    </>
                  ) : (
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
                      No resolved decisions yet — confirm or skip signals and your scorecard builds itself.
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<FactCheckIcon />}
                    onClick={() => navigate('/trades/decisions')}
                    sx={{ mt: 2, fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 0, borderColor: 'var(--dwlf-ink-20)', color: 'var(--dwlf-ink-60)', '&:hover': { borderColor: CYAN, color: CYAN } }}
                  >
                    Decision Quality
                  </Button>
                </CardContent>
              </Card>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ShowChartIcon />}
                onClick={() => navigate('/markets/overview')}
                sx={{ mt: 2, fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 0, borderColor: 'var(--dwlf-ink-20)', color: 'var(--dwlf-ink-60)', '&:hover': { borderColor: CYAN, color: CYAN } }}
              >
                Markets — today at a glance
              </Button>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Sector cohorts — which groups are moving in one direction (additive) */}
      {(cohortBreadth?.cohorts || []).length > 0 && (
        <Box mb={4}>
          <SectionTitle primer={COHORTS_PRIMER}>Sector cohorts</SectionTitle>
          <Paper sx={{ p: 2 }}>
            {[...cohortBreadth.cohorts].sort(cohortSort).map((c) => {
              const sig = COHORT_SIGNALS[c.signal] || COHORT_SIGNALS.split;
              const quiet = c.signal === 'split' || c.signal === 'insufficient';
              return (
                <Tooltip
                  key={c.cohort}
                  title={c.explain || COHORT_SIGNAL_FALLBACKS[c.signal] || COHORT_FALLBACK_GENERIC}
                  arrow
                  enterTouchDelay={0}
                  slotProps={TOOLTIP_SLOT_PROPS(360)}
                >
                  <Box
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, flexWrap: 'wrap',
                      opacity: quiet ? 0.55 : 1,
                      cursor: 'help',
                      borderBottom: '1px solid var(--dwlf-ink-06)',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography sx={{ fontFamily: GROTESK, fontSize: '0.9rem', fontWeight: 600, color: 'var(--dwlf-text)', minWidth: 110 }}>
                      {COHORT_NAMES[c.cohort] || c.cohort}
                    </Typography>
                    <Chip
                      label={sig.label}
                      size="small"
                      sx={{ backgroundColor: quiet ? 'var(--dwlf-ink-08)' : sig.color, color: quiet ? MUTED : 'var(--dwlf-bg)', fontFamily: MONO, fontSize: '0.65rem', fontWeight: 700, height: 20 }}
                    />
                    {Boolean(c.changed) && (
                      <Chip label="NEW" size="small" sx={{ backgroundColor: 'var(--dwlf-text)', color: 'var(--dwlf-bg)', fontFamily: MONO, fontSize: '0.6rem', fontWeight: 700, height: 18 }} />
                    )}
                    {Number.isFinite(c.daysInState) && c.daysInState > 0 && (
                      <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED }}>
                        {c.daysInState}{c.daysInStateIsFloor ? '+' : ''}d
                      </Typography>
                    )}
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, ml: 'auto' }}>
                    D {fmtCounts(c.counts?.daily)} · W {fmtCounts(c.counts?.weekly)}{c.lowN ? ' · low-n' : ''}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
            <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, mt: 1 }}>
              Daily + weekly swing-ladder bias per group · amber = distribution forming · cyan = cycle low forming
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Ranges — daily (tactical) + weekly (structural), toggle-selectable (additive) */}
      {allRanges.length > 0 && (
        <Box mb={4}>
          <SectionTitle
            primer={RANGES_PRIMER[rangeTf]}
            action={(
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {['daily', 'weekly'].map((tf) => {
                  const active = rangeTf === tf;
                  const count = allRanges.filter((r) => r.timeframe === tf).length;
                  return (
                    <Box
                      key={tf}
                      role="button"
                      aria-pressed={active}
                      aria-label={`${tf} ranges: ${count}`}
                      tabIndex={0}
                      onClick={() => setRangeTf(tf)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRangeTf(tf); } }}
                      sx={{
                        cursor: 'pointer', px: 1.25, py: 0.35, borderRadius: 1,
                        fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.04em', textTransform: 'capitalize', fontWeight: 700,
                        color: active ? 'var(--dwlf-bg)' : MUTED,
                        backgroundColor: active ? CYAN : 'var(--dwlf-ink-06)',
                        '&:hover': { backgroundColor: active ? CYAN : 'var(--dwlf-ink-12)' },
                        '&:focus-visible': { outline: `1px solid ${CYAN}`, outlineOffset: '2px' },
                      }}
                    >
                      {tf} {count}
                    </Box>
                  );
                })}
              </Box>
            )}
          >
            Ranges
          </SectionTitle>
          <Paper sx={{ p: 2 }}>
            {shownRanges.length === 0 ? (
              <Typography sx={{ fontFamily: MONO, fontSize: '0.8rem', color: MUTED }}>
                No {rangeTf} ranges right now
                {rangeTf === 'weekly' ? ' — most assets are trending on the weekly (no horizontal box).' : '.'}
              </Typography>
            ) : shownRanges.map((r) => {
              const liveState = r.live?.liveState;
              const st = RANGE_STATES[liveState] || RANGE_STATES.mid_range;
              // Derive quiet from the RESOLVED state, so an unknown backend
              // state gets the same dimmed treatment (not a bright mislabel).
              const quiet = st === RANGE_STATES.mid_range;
              const pos = Number.isFinite(r.live?.livePosition) ? Math.round(r.live.livePosition * 100) : null;
              // Prefer the LIVE break age (recomputed from candles, catches a
              // mid-week breakout) over the weekly snapshot. Only fall back to the
              // snapshot's age when it describes the SAME break direction as the
              // live state (their vocabularies differ: live above/below vs
              // snapshot broke_out_up/broke_down/retesting_*) — otherwise show no
              // age rather than one belonging to the opposite, stale break.
              const snap = r.snapshot?.state;
              const snapAgrees = (liveState === 'above' && (snap === 'broke_out_up' || snap === 'retesting_ceiling'))
                || (liveState === 'below' && (snap === 'broke_down' || snap === 'retesting_floor'));
              const broke = r.live?.brokeDaysAgo ?? (snapAgrees ? r.snapshot?.brokeDaysAgo : undefined);
              const isBreak = liveState === 'above' || liveState === 'below';
              // Only weekly overrides the chart's timeframe (`?timeframe=weekly`);
              // daily rows omit it so the user's saved chart preference stands.
              const path = chartPathFor(r.symbol, r.timeframe === 'weekly' ? 'weekly' : undefined);
              const go = () => { if (path) { navigate(path); } };
              return (
                <Box
                  key={`${r.timeframe}-${r.symbol}-${r.floor}-${r.ceiling}`}
                  role="button"
                  tabIndex={0}
                  onClick={go}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, flexWrap: 'wrap',
                    opacity: quiet ? 0.55 : 1,
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--dwlf-ink-06)',
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { backgroundColor: 'var(--dwlf-ink-04)' },
                    '&:focus-visible': { outline: `1px solid ${CYAN}`, outlineOffset: '-1px' },
                  }}
                >
                  <Typography sx={{ fontFamily: GROTESK, fontSize: '0.9rem', fontWeight: 600, color: 'var(--dwlf-text)', minWidth: 92 }}>
                    {r.symbol}
                  </Typography>
                  <Chip
                    label={st.label}
                    size="small"
                    sx={{ backgroundColor: quiet ? 'var(--dwlf-ink-08)' : st.color, color: quiet ? MUTED : 'var(--dwlf-bg)', fontFamily: MONO, fontSize: '0.65rem', fontWeight: 700, height: 20 }}
                  />
                  {!isBreak && pos !== null && (
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED }}>{pos}%</Typography>
                  )}
                  {isBreak && Number.isFinite(broke) && (
                    <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED }}>
                      {broke === 0 ? 'today' : `${broke}d ago`}
                    </Typography>
                  )}
                  <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, ml: 'auto' }}>
                    {fmtPrice(r.floor)}–{fmtPrice(r.ceiling)}
                  </Typography>
                </Box>
              );
            })}
            <Typography sx={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, mt: 1 }}>
              {rangeTf === 'weekly'
                ? 'Weekly cycle-pivot ranges · the structural, dead-money-sideways read (most assets trend on the weekly)'
                : 'Daily cycle-pivot ranges · position = where in the range (0% floor → 100% ceiling) · cyan = at floor (DCL-confluence watch)'}
              {' · tap a row for its chart'}
            </Typography>
          </Paper>
        </Box>
      )}

    </Container>
  );
};

export default TodayPage;
